'use client';
import React, { useMemo } from 'react';
import styles from './locations.module.css';
import { hashLocation } from '@scitrials-sst/core/Next/utils';
import Apply from '@/components/trial/trial-detail/side-tab/apply/apply';
import { getStudyStatus } from '@scitrials-sst/core/Next/lib';
import type { Location } from '@scitrials-sst/core/Next/types';

type LocationWithId = Location & { applyId: string };

export function LocationList({
	nctid,
	data,
	applied,
}: {
	nctid: string;
	data: Location[];
	applied: string[];
}) {
	if (!data || !nctid) return null;

	const locationsWithIds: LocationWithId[] = useMemo(() => {
		return data?.map((loc) => ({
			...loc,
			applyId: hashLocation(loc, 'sha256'),
		}));
	}, [data]);

	return (
		<div className={styles['location-cards']}>
			{locationsWithIds.map((item) => (
				<LocationCard
					key={item.applyId}
					item={item}
					applyId={item.applyId}
					disabled={applied.includes(item.applyId)}
					trialId={nctid}
				/>
			))}
		</div>
	);
}

export function LocationCard({
	item,
	applyId,
	disabled,
	trialId,
}: {
	item: Location;
	applyId: string;
	disabled: boolean;
	trialId: string;
}) {
	const status = getStudyStatus(item?.status ?? '');

	const contactList =
		item?.contacts?.filter((contact) => contact.role === 'CONTACT') ?? [];
	const investigatorList =
		item?.contacts?.filter(
			(contact) =>
				contact.role === 'PRINCIPAL_INVESTIGATOR' ||
				contact.role === 'SUB_INVESTIGATOR'
		) ?? [];

	return (
		<div className={styles['location-card']}>
			<div className={styles['location-title']}>
				{item?.facility} - {status.title}
			</div>

			{investigatorList && investigatorList.length > 0 && (
				<div className={styles['location-contact']}>
					<h2>Investigator {investigatorList.length > 1 ? 's' : ''}</h2>
					{investigatorList.map((inv, i) => (
						<p key={`${i}-${inv?.name}`}>
							{inv?.name} ({inv?.role?.replaceAll('_', ' ').toLocaleLowerCase()}
							)
						</p>
					))}
				</div>
			)}

			{contactList && contactList.length > 0 && (
				<div className={styles['location-contact']}>
					<h2>Contact</h2>
					{contactList.map((contact, i) => (
						<React.Fragment key={`${contact?.phone}-${i}`}>
							{contact?.name && (
								<p>
									{contact?.name}{' '}
									<a href={`mailto:${contact?.email}`} className={styles.a}>
										{contact?.email}
									</a>
								</p>
							)}
							{contact?.phone && (
								<p>
									Phone:{' '}
									<a href={`tel:${contact?.phone}`} className={styles.a}>
										{contact?.phone}
									</a>
								</p>
							)}
						</React.Fragment>
					))}
				</div>
			)}

			<div className={styles['location-address']}>
				<h2>Address</h2>
				<p>{item?.city}</p>
				<p>{item?.state}</p>
				<p>{item?.zip}</p>
				<p>{item?.country}</p>
			</div>

			{status.title === 'Recruiting' && (
				<div style={{ display: 'flex', justifyContent: 'end' }}>
					{/* <Apply prop={[trialId, applyId]} /> */}
					<Apply prop={[trialId, applyId]} disable={disabled} />
				</div>
			)}
		</div>
	);
}
